import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { ASSETS } from '../data/content';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from './ui/sheet';

const NAV = [
    { to: '/', key: 'home' },
    { to: '/about', key: 'about' },
    { to: '/history', key: 'history' },
    { to: '/leadership', key: 'leadership' },
    { to: '/capabilities', key: 'capabilities' },
    { to: '/strategy', key: 'strategy' },
    { to: '/projects', key: 'projects' },
    { to: '/global-presence', key: 'global' },
    { to: '/partnerships', key: 'partnerships' },
    { to: '/contact', key: 'contact' },
];

export const Header = () => {
    const { t, lang, toggleLang } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const closeMobile = () => setMobileOpen(false);

    const isHome = location.pathname === '/';
    const transparent = isHome && !scrolled;

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                transparent
                    ? 'bg-transparent border-b border-transparent'
                    : 'bg-navy-deep/95 backdrop-blur border-b border-gold/20'
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
                    <img
                        src={ASSETS.logo}
                        alt="Delta International Energy"
                        className="h-12 md:h-14 w-auto object-contain"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.key}
                            to={n.to}
                            data-testid={`nav-${n.key}`}
                            className={({ isActive }) =>
                                `text-[13px] tracking-wide transition-colors link-underline ${
                                    isActive ? 'text-gold' : 'text-white/85 hover:text-white'
                                }`
                            }
                        >
                            {t(`nav.${n.key}`)}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        data-testid="lang-toggle"
                        onClick={toggleLang}
                        className="hidden md:flex items-center gap-2 px-3.5 py-2 rounded-full border border-gold/40 text-gold text-[12px] tracking-widest uppercase hover:bg-gold hover:text-navy-deep transition-colors"
                    >
                        <Globe className="w-3.5 h-3.5" />
                        <span>{t('nav.switch_to')}</span>
                    </button>

                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="mobile-menu-toggle"
                                className="lg:hidden text-white p-2"
                                aria-label="Open menu"
                            >
                                <Menu className="w-6 h-6" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side={lang === 'ar' ? 'left' : 'right'} className="bg-navy-deep border-l border-gold/20 w-[300px] p-0">
                            <SheetTitle className="sr-only">Site Navigation</SheetTitle>
                            <div className="p-6 border-b border-gold/15 flex items-center justify-between">
                                <img
                                    src={ASSETS.logo}
                                    alt="Delta International Energy"
                                    className="h-10 w-auto object-contain"
                                />
                                <button onClick={() => setMobileOpen(false)} className="text-white/70">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <nav className="flex flex-col py-4">
                                {NAV.map((n) => (
                                    <NavLink
                                        key={n.key}
                                        to={n.to}
                                        onClick={closeMobile}
                                        data-testid={`mobile-nav-${n.key}`}
                                        className={({ isActive }) =>
                                            `px-6 py-3 text-[14px] tracking-wide border-b border-white/5 ${
                                                isActive ? 'text-gold' : 'text-white/85'
                                            }`
                                        }
                                    >
                                        {t(`nav.${n.key}`)}
                                    </NavLink>
                                ))}
                                <button
                                    onClick={toggleLang}
                                    data-testid="mobile-lang-toggle"
                                    className="mx-6 mt-6 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-gold/40 text-gold text-[12px] tracking-widest uppercase hover:bg-gold hover:text-navy-deep"
                                >
                                    <Globe className="w-3.5 h-3.5" />
                                    <span>{t('nav.switch_to')}</span>
                                </button>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default Header;
